import React, { useState, useMemo } from 'react';

/**
 * Tomato payout + REAL-PROFIT calculator. Answers two things for a restaurant:
 *   1) "what do I actually RECEIVE on Tomato?" (platform take), and
 *   2) "am I actually making a profit after food cost — or selling at a loss?"
 *
 * The key truth it proves (validated vs 2026 India benchmarks):
 *   • Raising menu prices to "cover" commission BACKFIRES — commission is charged on
 *     the inflated price, and the discount is also off the inflated price.
 *   • Commission (~25%) + 18% GST on commission + ads (~12%) + convenience (1.88%)
 *     ≈ 43% of the DISCOUNTED order value. After food+packaging (~28–38%), the
 *     contribution often goes negative.
 * Self-contained & embeddable. Pass `restaurantMarkups` (admin ciMarkups rows) to get
 * a per-restaurant picker that auto-loads that restaurant's real Tomato markup & offers.
 */

const money = (n) => '₹' + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const money0 = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const pct1 = (n) => (Math.round((Number(n) || 0) * 10) / 10) + '%';

export default function ZomatoPayoutCalculator({ restaurantMarkups }) {
  const [sale, setSale] = useState(100000);
  const [commission, setCommission] = useState(25);
  const [adsPct, setAdsPct] = useState(12);
  const [conv, setConv] = useState(1.88);
  const [markup, setMarkup] = useState(20);
  const [foodCost, setFoodCost] = useState(35);
  const [discType, setDiscType] = useState('pct');  // 'pct' | 'amt'
  const [disc, setDisc] = useState(50);
  const [gstOnFees, setGstOnFees] = useState(true);  // 18% on commission + ads
  const [gstOnConv, setGstOnConv] = useState(false); // 18% on convenience fee
  const [applyTds, setApplyTds] = useState(true);    // 0.1% TDS 194-O
  const [fSettle, setFSettle] = useState(3);         // FoodWinc settlement fee %
  const [restaurantName, setRestaurantName] = useState('');
  const [offersHint, setOffersHint] = useState([]);
  const [selectedRid, setSelectedRid] = useState('');

  const hasPicker = Array.isArray(restaurantMarkups) && restaurantMarkups.length > 0;
  const pick = (rid) => {
    setSelectedRid(rid);
    const row = (restaurantMarkups || []).find((x) => x.restaurantId === rid);
    if (row) {
      setRestaurantName(row.restaurantName || '');
      if (row.zomato && row.zomato.avg != null) setMarkup(Math.round(row.zomato.avg));
      setOffersHint(row.zomatoOffers || []);
    } else { setRestaurantName(''); setOffersHint([]); }
  };

  const c = useMemo(() => {
    const S = Math.max(0, Number(sale) || 0);          // Tomato menu value billed (inflated), pre-discount
    const dRaw = Math.max(0, Number(disc) || 0);
    const D = discType === 'amt' ? Math.min(S, dRaw) : S * Math.min(100, dRaw) / 100;
    const NOV = Math.max(0, S - D);                    // net order value = commission base = what customer pays for food
    // platform take, all on NOV
    const rate = (Number(commission) || 0) / 100 * (gstOnFees ? 1.18 : 1)
      + (Number(adsPct) || 0) / 100 * (gstOnFees ? 1.18 : 1)
      + (Number(conv) || 0) / 100 * (gstOnConv ? 1.18 : 1)
      + (applyTds ? 0.001 : 0);
    const comm = NOV * (Number(commission) || 0) / 100;
    const adsAmt = NOV * (Number(adsPct) || 0) / 100;
    const gstFees = gstOnFees ? (comm + adsAmt) * 0.18 : 0;
    const convAmt = NOV * (Number(conv) || 0) / 100;
    const convGst = gstOnConv ? convAmt * 0.18 : 0;
    const tds = applyTds ? NOV * 0.001 : 0;
    const totalDeduct = comm + adsAmt + gstFees + convAmt + convGst + tds;
    const receive = Math.max(0, NOV - totalDeduct);
    const takePctOfNov = NOV > 0 ? totalDeduct / NOV * 100 : 0;
    // un-inflate to the NORMAL price → food cost is tied to actual food, not the listed price
    const m = Math.max(0, Number(markup) || 0);
    const base = S / (1 + m / 100);                    // your normal (pre-Tomato-markup) menu value
    const k = Math.max(0, Number(foodCost) || 0);
    const cost = base * k / 100;                       // food + packaging cost (absolute ₹)
    const profit = receive - cost;                     // true profit on Tomato
    const marginPct = base > 0 ? profit / base * 100 : 0;
    // break-even discount: max discount before you sell below food cost (given markup+fees)
    const beDiscount = (S > 0 && rate < 1) ? 100 * (1 - cost / (S * (1 - rate))) : 0;
    // FoodWinc: sell at your NORMAL price, 0% commission, settlement(+18% GST)+0.1% TDS, no forced discount
    const fwRate = (Number(fSettle) || 0) / 100 * 1.18 + 0.001;
    const fwReceive = Math.max(0, base * (1 - fwRate));
    const fwProfit = fwReceive - cost;
    const profitSwing = fwProfit - profit;
    return {
      S, D, NOV, comm, adsAmt, gstFees, convAmt, convGst, tds, totalDeduct, receive, takePctOfNov, rate,
      m, base, k, cost, profit, marginPct, beDiscount, fwRate, fwReceive, fwProfit, profitSwing,
    };
  }, [sale, commission, adsPct, conv, markup, foodCost, discType, disc, gstOnFees, gstOnConv, applyTds, fSettle]);

  const isLoss = c.profit < 0;

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.04)' };
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4 };
  const input = { width: '100%', padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const td = { padding: '8px 10px', fontSize: 13.5, borderBottom: '1px solid #f2f2f2' };
  const tdR = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const chk = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#334155', marginTop: 8 };
  const feeRow = (name, amount, note, strong) => (
    <tr>
      <td style={td}>{name}{note ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> {note}</span> : null}</td>
      <td style={{ ...tdR, color: '#b91c1c', fontWeight: strong ? 700 : 400 }}>{amount ? '−' + money(amount) : '—'}</td>
    </tr>
  );

  return (
    <div>
      {hasPicker ? (
        <div style={{ ...card, marginBottom: 16 }}>
          <label style={label}>Load a restaurant (auto-fills its real Tomato markup &amp; offers)</label>
          <select value={selectedRid} onChange={(e) => pick(e.target.value)} style={input}>
            <option value="">— Manual / pick a restaurant —</option>
            {restaurantMarkups.map((r) => (
              <option key={r.restaurantId} value={r.restaurantId}>
                {r.restaurantName}{r.zomato && r.zomato.avg != null ? ` — Tomato +${r.zomato.avg}%` : ' — no Tomato data'}
              </option>
            ))}
          </select>
          {offersHint.length ? <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>Their Tomato offers: {offersHint.join(' · ')}</div> : null}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) minmax(280px,1fr)', gap: 16, alignItems: 'start' }}>
        {/* Inputs */}
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 12, color: '#0F172A' }}>Your Tomato numbers{restaurantName ? ` — ${restaurantName}` : ''}</div>
          <div style={{ marginBottom: 12 }}>
            <label style={label}>Tomato sales (₹) <span style={{ color: '#94A3B8', fontWeight: 400 }}>— menu value for the period (e.g. a month)</span></label>
            <input type="number" min="0" value={sale} onChange={(e) => setSale(e.target.value)} style={input} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={label}>Your discount</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="number" min="0" value={disc} onChange={(e) => setDisc(e.target.value)} style={{ ...input, flex: 1 }} />
                <select value={discType} onChange={(e) => setDiscType(e.target.value)} style={{ ...input, width: 64, padding: '9px 6px' }}>
                  <option value="pct">%</option>
                  <option value="amt">₹</option>
                </select>
              </div>
            </div>
            <div><label style={label}>Price markup on Tomato %</label><input type="number" min="0" step="1" value={markup} onChange={(e) => setMarkup(e.target.value)} style={input} title="How much you raised your Tomato prices vs your normal price to 'cover' commission" /></div>
            <div><label style={label}>Commission %</label><input type="number" min="0" step="0.1" value={commission} onChange={(e) => setCommission(e.target.value)} style={input} /></div>
            <div><label style={label}>Ad commission %</label><input type="number" min="0" step="0.1" value={adsPct} onChange={(e) => setAdsPct(e.target.value)} style={input} title="Tomato ads spend as a % of sales (0 if you don't run ads)" /></div>
            <div><label style={label}>Convenience fee %</label><input type="number" min="0" step="0.01" value={conv} onChange={(e) => setConv(e.target.value)} style={input} /></div>
            <div><label style={label}>Food + packaging cost %</label><input type="number" min="0" step="1" value={foodCost} onChange={(e) => setFoodCost(e.target.value)} style={input} title="Your food + packaging cost as a % of your NORMAL (pre-markup) price. Typical 28–38%." /></div>
          </div>
          <div style={{ marginTop: 12, borderTop: '1px dashed #e5e7eb', paddingTop: 10 }}>
            <label style={chk}><input type="checkbox" checked={gstOnFees} onChange={(e) => setGstOnFees(e.target.checked)} /> Add 18% GST on commission + ads <span style={{ color: '#94A3B8' }}>(real; claimable as input credit)</span></label>
            <label style={chk}><input type="checkbox" checked={gstOnConv} onChange={(e) => setGstOnConv(e.target.checked)} /> Add 18% GST on the convenience fee</label>
            <label style={chk}><input type="checkbox" checked={applyTds} onChange={(e) => setApplyTds(e.target.checked)} /> Deduct TDS 0.1% (194-O) <span style={{ color: '#94A3B8' }}>(claimable against income tax)</span></label>
          </div>
        </div>

        {/* THE VERDICT — profit after food cost */}
        <div style={{ ...card, background: isLoss ? 'linear-gradient(135deg,#fef2f2,#fff1f2)' : 'linear-gradient(135deg,#ecfdf5,#f0fdf4)', border: `1px solid ${isLoss ? '#fecaca' : '#bbf7d0'}` }}>
          <div style={{ fontSize: 12, color: isLoss ? '#b91c1c' : '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {isLoss ? '🔴 You are selling at a LOSS on Tomato' : '🟢 Your real profit on Tomato'}
          </div>
          <div style={{ fontSize: 40, fontWeight: 900, color: isLoss ? '#b91c1c' : '#15803d', marginTop: 6, lineHeight: 1.05 }}>
            {isLoss ? '−' : '+'}{money0(Math.abs(c.profit))}
          </div>
          <div style={{ fontSize: 13, color: isLoss ? '#9f1239' : '#166534', marginTop: 4 }}>
            after food+packaging cost of {money0(c.cost)} · margin <b>{pct1(c.marginPct)}</b> of your real sales
          </div>
          <div style={{ marginTop: 12, background: '#fff', borderRadius: 10, padding: '10px 12px', border: `1px solid ${isLoss ? '#fecaca' : '#bbf7d0'}` }}>
            <div style={{ fontSize: 13, color: '#334155' }}>
              You list <b>{money0(c.S)}</b> (+{pct1(c.m)} markup){c.D > 0 ? <> then discount <b>{money0(c.D)}</b></> : null} → customer pays <b>{money0(c.NOV)}</b> → Tomato takes <b>{money0(c.totalDeduct)}</b> ({pct1(c.takePctOfNov)}) → you keep <b>{money0(c.receive)}</b>, but your food cost is <b>{money0(c.cost)}</b>.
            </div>
            <div style={{ fontSize: 12.5, color: isLoss ? '#b91c1c' : '#166534', marginTop: 8, fontWeight: 600 }}>
              {c.beDiscount >= 0
                ? <>⚖️ Break-even: any discount above <b>{pct1(c.beDiscount)}</b> and you sell below food cost. You're giving <b>{discType === 'pct' ? pct1(Number(disc)) : money0(c.D)}</b>.</>
                : <>Even at 0% discount you can't cover food cost at these fees.</>}
            </div>
          </div>
        </div>
      </div>

      {/* Fallacy buster */}
      <div style={{ ...card, marginTop: 16, background: '#FFFBEB', border: '1px solid #FDE68A' }}>
        <div style={{ fontWeight: 800, color: '#92400E', marginBottom: 6 }}>💡 Why raising prices doesn't save you</div>
        <div style={{ fontSize: 13.5, color: '#92400E', lineHeight: 1.6 }}>
          You raised prices <b>+{pct1(c.m)}</b> to "cover" the {pct1(commission)} commission — but commission is charged on the <b>higher</b> price, and your <b>{discType === 'pct' ? pct1(Number(disc)) : money0(c.D)}</b> discount is also off the higher price. So the markup is discounted away <i>and</i> taxed by commission. Commission + 18% GST + ads + convenience = <b>{pct1(c.takePctOfNov)}</b> of what the customer pays, before you buy a single ingredient.
        </div>
      </div>

      {/* Breakdown */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10, color: '#0F172A' }}>Where the money goes</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={td}>Your normal menu value <span style={{ color: '#9ca3af', fontWeight: 400 }}>(before markup)</span></td><td style={tdR}>{money(c.base)}</td></tr>
              <tr><td style={td}><b>Tomato listed value</b> <span style={{ color: '#9ca3af', fontWeight: 400 }}>(+{pct1(c.m)} markup)</span></td><td style={{ ...tdR, fontWeight: 700 }}>{money(c.S)}</td></tr>
              {c.D > 0 ? <tr><td style={td}>Your discount (restaurant-funded)</td><td style={{ ...tdR, color: '#b91c1c' }}>−{money(c.D)}</td></tr> : null}
              <tr style={{ background: '#fafafa' }}><td style={td}><b>Net order value</b> <span style={{ color: '#9ca3af', fontWeight: 400 }}>(customer pays · commission base)</span></td><td style={{ ...tdR, fontWeight: 700 }}>{money(c.NOV)}</td></tr>
              {feeRow('Commission', c.comm, pct1(commission))}
              {Number(adsPct) > 0 ? feeRow('Ads', c.adsAmt, pct1(adsPct)) : null}
              {gstOnFees ? feeRow('GST on commission + ads', c.gstFees, '18%') : null}
              {feeRow('Convenience / payment fee', c.convAmt, pct1(conv))}
              {gstOnConv ? feeRow('GST on convenience fee', c.convGst, '18%') : null}
              {applyTds ? feeRow('TDS (194-O)', c.tds, '0.1%') : null}
              <tr style={{ background: '#f8fafc' }}><td style={td}><b>You receive</b></td><td style={{ ...tdR, fontWeight: 700 }}>{money(c.receive)}</td></tr>
              {feeRow('Food + packaging cost', c.cost, pct1(c.k) + ' of normal price')}
              <tr style={{ fontWeight: 800, background: isLoss ? '#fef2f2' : '#f0fdf4' }}>
                <td style={td}>{isLoss ? 'Your LOSS' : 'Your profit'}</td>
                <td style={{ ...tdR, color: isLoss ? '#b91c1c' : '#15803d', fontSize: 15 }}>{isLoss ? '−' : '+'}{money(Math.abs(c.profit))}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
          Commission &amp; ads are charged on the net order value (after your discount). 18% GST on the fees is claimable as input credit. TDS 0.1% is claimable against income tax. TCS does not apply to restaurant orders (Tomato pays the 5% food GST u/s 9(5)). Food + packaging cost is a fixed ₹ per plate — it does not shrink when you inflate the listed price, which is why the markup can't rescue the margin. Delivery &amp; platform fees are customer-paid.
        </div>
      </div>

      {/* FoodWinc comparison */}
      <div style={{ ...card, marginTop: 16, background: 'linear-gradient(135deg,#ecfdf5,#f0fdf4)', border: '1px solid #bbf7d0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Same food on FoodWinc</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: c.fwProfit >= 0 ? '#15803d' : '#b91c1c', marginTop: 4, lineHeight: 1.1 }}>{c.fwProfit >= 0 ? '+' : '−'}{money0(Math.abs(c.fwProfit))}</div>
            <div style={{ fontSize: 13, color: '#166534' }}>profit at your <b>normal price</b> ({money0(c.base)}) — <b>0% commission</b>, no forced discount, only {pct1(fSettle)} settlement + GST + 0.1% TDS</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#64748B' }}>Profit swing vs Tomato</div>
            <div style={{ fontSize: 30, fontWeight: 900, color: '#15803d' }}>+{money0(c.profitSwing)}</div>
            <div style={{ fontSize: 12, color: '#166534' }}>more profit on the same sales</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <label style={{ ...label, marginBottom: 0 }}>FoodWinc settlement %</label>
          <input type="number" min="0" step="0.1" value={fSettle} onChange={(e) => setFSettle(e.target.value)} style={{ ...input, width: 90, padding: '6px 8px' }} />
          <span style={{ fontSize: 11.5, color: '#94a3b8' }}>On FoodWinc you sell at your real price (no markup needed) and don't fund forced discounts — so the same food earns far more.</span>
        </div>
      </div>
    </div>
  );
}
